import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface StatsModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    isFetchingStats: boolean;
    statsData: any;
}

const StatsModal: React.FC<StatsModalProps> = ({
    isOpen,
    onOpenChange,
    isFetchingStats,
    statsData
}) => {
    const [activeTab, setActiveTab] = useState<string>("last-join");

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Statistiques de la Classe</DialogTitle>
                    <DialogDescription>
                        Suivi des invitations et de la participation.
                    </DialogDescription>
                </DialogHeader>
                {isFetchingStats ? (
                    <div className="h-64 flex items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : statsData && (
                    <div className="space-y-8 py-4">
                        <div className="grid grid-cols-4 gap-4">
                            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-4 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Ciblés</p>
                                <p className="text-2xl font-black text-slate-900">{statsData.targeted}</p>
                            </div>
                            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-4 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Envoyés</p>
                                <p className="text-2xl font-black text-emerald-600">{statsData.sent}</p>
                            </div>
                            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-4 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Échecs</p>
                                <p className="text-2xl font-black text-red-500">{statsData.failed}</p>
                            </div>
                            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-4 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Présents</p>
                                <p className="text-2xl font-black text-primary">{statsData.joined}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-4 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Total Accès</p>
                                <p className="text-2xl font-black text-blue-600">{statsData.total_access_attempts || 0}</p>
                            </div>
                            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-4 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Visiteurs Uniques</p>
                                <p className="text-2xl font-black text-indigo-600">{statsData.unique_accessors || 0}</p>
                            </div>
                            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-4 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Visites Répétées</p>
                                <p className="text-2xl font-black text-purple-600">
                                    {(statsData.total_access_attempts || 0) - (statsData.unique_accessors || 0)}
                                </p>
                            </div>
                            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-4 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Refusés</p>
                                <p className="text-2xl font-black text-orange-600">{statsData.denied_attempts || 0}</p>
                            </div>
                        </div>

                        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="last-join">Dernière Présence</TabsTrigger>
                                <TabsTrigger value="all-access">Tous les Accès</TabsTrigger>
                            </TabsList>

                            <TabsContent value="last-join" className="space-y-3">
                                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest px-1">Dernières présences</h4>
                                <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
                                    <Table>
                                        <TableHeader className="bg-slate-50">
                                            <TableRow>
                                                <TableHead className="text-[10px] font-bold h-10">Étudiant</TableHead>
                                                <TableHead className="text-[10px] font-bold h-10">Email</TableHead>
                                                <TableHead className="text-[10px] font-bold h-10 text-right">Heure d'entrée</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {statsData.recent_joins.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={3} className="text-center py-8 text-xs text-slate-400 italic">Aucun étudiant n'a rejoint la salle pour le moment.</TableCell>
                                                </TableRow>
                                            ) : (
                                                statsData.recent_joins.map((join: any, i: number) => (
                                                    <TableRow key={i}>
                                                        <TableCell className="text-xs font-bold text-slate-700">{join.first_name} {join.last_name}</TableCell>
                                                        <TableCell className="text-xs text-slate-500">{join.email}</TableCell>
                                                        <TableCell className="text-xs text-slate-400 text-right font-medium">{new Date(join.last_joined_at).toLocaleTimeString()}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </TabsContent>

                            <TabsContent value="all-access" className="space-y-3">
                                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest px-1">Historique des accès</h4>
                                <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
                                    <Table>
                                        <TableHeader className="bg-slate-50">
                                            <TableRow>
                                                <TableHead className="text-[10px] font-bold h-10">Étudiant</TableHead>
                                                <TableHead className="text-[10px] font-bold h-10">Email</TableHead>
                                                <TableHead className="text-[10px] font-bold h-10">Adresse IP</TableHead>
                                                <TableHead className="text-[10px] font-bold h-10 text-right">Heure d'accès</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(statsData.recent_access_logs || []).length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-8 text-xs text-slate-400 italic">Aucun accès enregistré pour le moment.</TableCell>
                                                </TableRow>
                                            ) : (
                                                (statsData.recent_access_logs || []).map((log: any, i: number) => (
                                                    <TableRow key={i}>
                                                        <TableCell className="text-xs font-bold text-slate-700">{log.first_name} {log.last_name}</TableCell>
                                                        <TableCell className="text-xs text-slate-500">{log.email}</TableCell>
                                                        <TableCell className="text-xs text-slate-500 font-mono">{log.ip_address || 'N/A'}</TableCell>
                                                        <TableCell className="text-xs text-slate-400 text-right font-medium">{new Date(log.accessed_at).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default StatsModal;
